import { Badge } from '../ui/Badge';
import type { AlertCategory, AlertType, AlertTypeInfo } from '../../hooks/useDashboardAlerts';
import type { DashboardAlertSummary } from '../../lib/api';
import { getAlertCount, getAlertsByCategory, getCategoryTotal, getSeverityVariant } from '../../hooks/useDashboardAlerts';

interface AlertCategoryCardProps {
  category: AlertCategory;
  summary: DashboardAlertSummary | undefined;
  selectedAlert: AlertType | null;
  onAlertSelect: (alertType: AlertType) => void;
  isLoading?: boolean;
}

// Category display configuration
const CATEGORY_CONFIG: Record<AlertCategory, { label: string; variant: 'warning' | 'earth' | 'danger' }> = {
  admin: { label: 'ADMIN', variant: 'warning' },
  clients: { label: 'CLIENTS', variant: 'earth' },
  workorders: { label: 'WORKORDERS', variant: 'danger' },
};

export function AlertCategoryCard({
  category,
  summary,
  selectedAlert,
  onAlertSelect,
  isLoading,
}: AlertCategoryCardProps) {
  const config = CATEGORY_CONFIG[category];
  const alerts = getAlertsByCategory(category);
  const categoryTotal = getCategoryTotal(summary, category);

  return (
    <div className="bg-white rounded-2xl border border-sage-200 overflow-hidden">
      {/* Category Header */}
      <div className="px-4 py-3 border-b border-sage-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-display font-semibold text-forest-900">{config.label}</h3>
          {isLoading ? (
            <div className="w-6 h-6 rounded-full bg-sage-100 animate-pulse" />
          ) : (
            <Badge variant={config.variant} size="sm">
              {categoryTotal}
            </Badge>
          )}
        </div>
      </div>

      {/* Alert Items */}
      <div className="divide-y divide-sage-100">
        {alerts.map((alert) => (
          <AlertItem
            key={alert.id}
            alert={alert}
            count={getAlertCount(summary, alert.id)}
            isSelected={selectedAlert === alert.id}
            onClick={() => onAlertSelect(alert.id)}
            isLoading={isLoading}
          />
        ))}
      </div>
    </div>
  );
}

interface AlertItemProps {
  alert: AlertTypeInfo;
  count: number;
  isSelected: boolean;
  onClick: () => void;
  isLoading?: boolean;
}

function AlertItem({ alert, count, isSelected, onClick, isLoading }: AlertItemProps) {
  // Don't show alerts with 0 count as clickable (but still show them)
  const hasItems = count > 0;
  const badgeVariant = hasItems ? getSeverityVariant(alert.severity) : 'default';

  return (
    <button
      onClick={onClick}
      disabled={!hasItems && !isLoading}
      className={`
        w-full px-4 py-3.5 sm:py-3 text-left transition-colors flex items-center justify-between min-h-touch
        ${isSelected
          ? 'bg-forest-50 border-l-2 border-l-forest-600'
          : hasItems
            ? 'hover:bg-sage-50 cursor-pointer active:bg-sage-100'
            : 'opacity-50 cursor-default'
        }
      `}
    >
      <div className="flex items-center gap-2">
        <span className="text-base">{alert.icon}</span>
        <span className={`text-sm ${isSelected ? 'font-medium text-forest-900' : 'text-forest-700'}`}>
          {alert.label}
        </span>
      </div>

      {isLoading ? (
        <div className="w-8 h-5 rounded bg-sage-100 animate-pulse" />
      ) : (
        <Badge
          variant={badgeVariant}
          size="sm"
        >
          {count}
        </Badge>
      )}
    </button>
  );
}
